import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../axiosInstance';
import { LanguageContext } from '../LanguageContext';
import AuthContext from '../AuthContext';
import ApplicationCard from '../components/ApplicationCard';
import InterventionCard from '../components/InterventionCard';
import { useWorkflowSchema, WORKFLOW_SCHEMA_STATUS } from '../hooks/useWorkflowSchema';
import { getParticipantStatusPresentation } from '../utils/participantStatus';

/**
 * Dynamic User Dashboard (replacement for legacy MyAppointments)
 * Responsibilities:
 *  - List submitted applications for the signed-in user
 *  - Surface a single in‑progress draft (dynamic workflow) if present
 *  - Start a brand new dynamic application workflow
 *  - Resume a saved draft at its last recorded step (history tail)
 *  - Provide basic messaging / notification hooks (extensible)
 *
 * NOTE: Routing (e.g. /user/dashboard) must be wired in App.js separately.
 */

const modalText = {
  en: {
    title: 'Saved application found',
    body: 'You already have a saved application in progress. Continue where you left off, or delete it and start again.',
    resume: 'Continue application',
    startOver: 'Delete and start again',
    cancel: 'Cancel',
    deleteError: 'We could not delete the saved application. Please try again.'
  },
  fr: {
    title: 'Demande sauvegardée trouvée',
    body: 'Vous avez déjà une demande sauvegardée en cours. Reprenez là où vous vous êtes arrêté ou supprimez-la pour recommencer.',
    resume: 'Reprendre la demande',
    startOver: 'Supprimer et recommencer',
    cancel: 'Annuler',
    deleteError: 'Impossible de supprimer la demande sauvegardée. Veuillez réessayer.'
  }
};

const deleteModalText = {
  en: {
    title: 'Delete saved application?',
    body: 'Deleting this saved application is permanent. You will need to start a new application to continue.',
    confirm: 'Delete saved application',
    cancel: 'Cancel',
    error: 'We could not delete the saved application. Please try again.'
  },
  fr: {
    title: 'Supprimer la demande sauvegardée?',
    body: 'La suppression de cette demande sauvegardée est définitive. Vous devrez commencer une nouvelle demande pour continuer.',
    confirm: 'Supprimer la demande sauvegardée',
    cancel: 'Annuler',
    error: 'Impossible de supprimer la demande sauvegardée. Veuillez réessayer.'
  }
};

const APPLICATION_CARD_STATUSES = new Set([
  'review_in_progress',
  'action_required',
  'support_active',
  'application_update',
  'application_history',
  'approved',
  'not_approved',
  'closed',
]);
const ACTIVE_SUBMISSION_STATUSES = new Set(['review_in_progress', 'action_required']);

const normalizeExternalStatus = (status) => {
  if (!status) return null;
  const token = String(status).trim().toLowerCase();
  if (!token) return null;
  return token.replace(/\s+/g, '_');
};

const submissionBlocksNewApplication = (submission) => {
  if (!submission || typeof submission !== 'object') return false;
  if (typeof submission.blocks_new_application === 'boolean') return submission.blocks_new_application;
  if (typeof submission.blocksNewApplication === 'boolean') return submission.blocksNewApplication;
  return ACTIVE_SUBMISSION_STATUSES.has(normalizeExternalStatus(submission.external_status));
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
};

const getSubmissionSortKey = (item) => {
  const createdAt = parseDateValue(item?.submitted_at || item?.created_at);
  const lastUpdated = parseDateValue(item?.last_updated || item?.updated_at);
  if (createdAt) {
    return { primary: createdAt, secondary: lastUpdated || 0 };
  }
  return { primary: lastUpdated || 0, secondary: 0 };
};

const pickMostRecentSubmission = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return [...items].sort((a, b) => {
    const aKey = getSubmissionSortKey(a);
    const bKey = getSubmissionSortKey(b);
    if (bKey.primary !== aKey.primary) return bKey.primary - aKey.primary;
    return bKey.secondary - aKey.secondary;
  })[0];
};

function UserDashboard({ suppressMessageBanner = false }) {
  const { language } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const modalCopy = modalText[language] || modalText.en;
  const deleteModalCopy = deleteModalText[language] || deleteModalText.en;
  const { status: schemaStatus, schema, error: schemaError, refresh: refreshSchema } = useWorkflowSchema();
  const workflowSchema = Array.isArray(schema) ? schema : [];
  const schemaReady = schemaStatus === WORKFLOW_SCHEMA_STATUS.READY && workflowSchema.length > 0;
  const schemaLoading = schemaStatus === WORKFLOW_SCHEMA_STATUS.LOADING || schemaStatus === WORKFLOW_SCHEMA_STATUS.IDLE;
  const schemaErrorState = schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR || (schemaStatus === WORKFLOW_SCHEMA_STATUS.READY && workflowSchema.length === 0);

  // Data state
  const [submissions, setSubmissions] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [interventionsLoading, setInterventionsLoading] = useState(false);
  const [interventionsError, setInterventionsError] = useState(null);
  const [draft, setDraft] = useState(null); // current draft object (dynamic)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasUrgentMessages, setHasUrgentMessages] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);

  const [showDraftModal, setShowDraftModal] = useState(false);
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState(null);
  const modalRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const deleteModalRef = useRef(null);

  useEffect(() => {
    if (showDraftModal && modalRef.current) {
      modalRef.current.focus();
    }
  }, [showDraftModal]);

  useEffect(() => {
    if (showDeleteModal && deleteModalRef.current) {
      deleteModalRef.current.focus();
    }
  }, [showDeleteModal]);

  // Fetch submitted applications + draft in parallel
  const loadData = async () => {
    if (!user || !user.id) {
      setError(language === 'fr' ? 'Session expirée.' : 'Session expired.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Use new dynamic draft endpoint; and fetch submitted submissions
      const draftPromise = api.get('/api/draft')
        .then(r => ({ status: r.status, data: r.data }))
        .catch(err => ({ status: err?.response?.status || 0, data: null }));
      const submissionsPromise = api.get('/api/submissions')
        .then(r => ({ status: r.status, data: r.data }))
        .catch(err => ({ status: err?.response?.status || 0, data: [] }));
      const messagesPromise = api
        .get('/api/messages/summary')
        .then(r => ({ data: r.data || {} }))
        .catch(() => ({ data: {} }));
      const [draftResp, submissionsResp, messagesResp] = await Promise.all([draftPromise, submissionsPromise, messagesPromise]);
      const subs = submissionsResp.status === 200 && Array.isArray(submissionsResp.data) ? submissionsResp.data : [];
      const normalizedSubs = subs.map((submission) => ({
        ...submission,
        external_status: normalizeExternalStatus(submission.external_status)
      }));
      setSubmissions(normalizedSubs);
      const d = (draftResp.status === 200) ? draftResp.data : null;
      setDraft(d ? { ...d, last_updated: d.updatedAt } : null);
      setUnreadCount(Number(messagesResp.data?.unreadCount) || 0);
      setHasUrgentMessages((Number(messagesResp.data?.urgentUnreadCount) || 0) > 0);
    } catch (e) {
      console.error('[dashboard] load failed', e);
      setError(language === 'fr' ? 'Échec du chargement.' : 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadData();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const normalizedSubmissions = Array.isArray(submissions)
    ? submissions.map((submission) => ({
      ...submission,
      external_status: normalizeExternalStatus(submission.external_status)
    }))
    : [];
  const applicationCardSubmissions = normalizedSubmissions.filter((submission) => (
    APPLICATION_CARD_STATUSES.has(normalizeExternalStatus(submission.external_status))
  ));
  const applicationCardSubmission = pickMostRecentSubmission(applicationCardSubmissions);
  const hasBlockingSubmissions = normalizedSubmissions.some(submissionBlocksNewApplication);
  const visibleDraft = hasBlockingSubmissions ? null : draft;
  const showApplicationCard = Boolean(applicationCardSubmission);
  const showStartButton = !visibleDraft && !hasBlockingSubmissions;

  useEffect(() => {
    if (!user?.id) return;
    if (normalizedSubmissions.length === 0) {
      setInterventions([]);
      setInterventionsLoading(false);
      setInterventionsError(null);
      return;
    }
    let cancelled = false;
    setInterventionsLoading(true);
    setInterventionsError(null);
    api.get('/api/my/interventions')
      .then((response) => {
        if (cancelled) return;
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];
        setInterventions(items);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[dashboard] interventions load failed', err);
        setInterventions([]);
        setInterventionsError(language === 'fr'
          ? 'Impossible de charger vos interventions. Veuillez réessayer.'
          : 'We could not load your interventions. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setInterventionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [normalizedSubmissions.length, user?.id, language]);

  // Start a new dynamic application workflow
  const startFreshFlow = async () => {
    try {
      await api.get('/api/draft').catch(()=>{});
      try { localStorage.setItem('iset.flow', 'dynamic'); } catch(_) {}
    } catch (e) {
      console.warn('[dashboard] init dynamic flow failed (non-fatal)', e);
    }
    navigate('/iset-wizard');
  };

  const handleStart = () => {
    if (!user || !user.id) {
      alert(language === 'fr' ? 'Veuillez vous reconnecter.' : 'Please log in again.');
      return;
    }
    if (!schemaReady) {
      alert(language === 'fr'
        ? 'Le formulaire dynamique est temporairement indisponible. R�essayez plus tard.'
        : 'The dynamic application form is temporarily unavailable. Please try again later.');
      return;
    }
    if (visibleDraft) {
      setModalError(null);
      setShowDraftModal(true);
      return;
    }
    startFreshFlow();
  };

  // Resume an existing draft - rely on its history tail for last step path
  const handleResumeDraft = async () => {
    if (!schemaReady) {
      alert(language === "fr"
        ? "Le schéma de formulaire est indisponible. Réessayez plus tard."
        : "The workflow schema is unavailable right now. Please try again later.");
      return;
    }
    try {
      const resp = await api.post('/api/draft/resume');
      if (!resp.data) {
        alert(language === 'fr' ? 'Aucun brouillon trouvé.' : 'No draft found.');
        return;
      }
      const d = resp.data;
      const target = d.stepCursor || workflowSchema?.[0]?.stepId || 'consent';
      // Always route through iset-wizard with step query parameter (stepIds are not standalone routes)
      navigate(`/iset-wizard?step=${encodeURIComponent(target)}`);
    } catch (e) {
      console.error('[dashboard] resume draft failed', e);
      alert(language === 'fr' ? 'Impossible de reprendre le brouillon.' : 'Could not resume draft.');
    }
  };
  const handleModalCancel = () => {
    if (modalBusy) return;
    setShowDraftModal(false);
    setModalError(null);
    setModalBusy(false);
  };

  const handleModalContinue = async () => {
    setModalBusy(true);
    setShowDraftModal(false);
    setModalError(null);
    try {
      await handleResumeDraft();
    } finally {
      setModalBusy(false);
    }
  };

  const handleModalStartFresh = async () => {
    setModalBusy(true);
    setModalError(null);
    try {
      await api.delete('/api/draft');
      setDraft(null);
      await startFreshFlow();
      setShowDraftModal(false);
    } catch (e) {
      console.error('[dashboard] failed to discard draft', e);
      setModalError(modalCopy.deleteError);
      setModalBusy(false);
      return;
    }
    setModalBusy(false);
  };

  const handleDeleteRequest = () => {
    if (deleteBusy) return;
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    if (deleteBusy) return;
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.delete('/api/draft');
      setDraft(null);
      setShowDeleteModal(false);
    } catch (err) {
      console.error('[dashboard] failed to delete draft', err);
      setDeleteError(deleteModalCopy.error);
    } finally {
      setDeleteBusy(false);
    }
  };


  const t = {
    en: {
      title: 'My PATH dashboard',
      start: 'Start application',
      loading: 'Loading...',
      unread: 'You have unread messages.',
      urgent: 'You have urgent messages. Please review them as soon as possible.',
      applicationHeading: 'Application',
      applicationInReview: 'Your application has been received. No action is needed at this time.',
      applicationActionRequired: 'There is an update on your application. Check your messages for next steps.',
      applicationApproved: 'Your current PATH support information is available here.',
      applicationNotApproved: 'NWAC has an update about your application. Please read your messages or contact us for details.',
      applicationClosed: 'This application is now in your history. You can still review its details below.',
      viewMessages: 'View messages',
      savedDraftHeading: 'Saved application',
      interventionsHeading: 'Current plan activities',
      interventionsLoading: 'Loading plan activities...',
      interventionsEmpty: 'There are no current plan activities right now.',
      interventionsError: 'We could not load your plan activities. Please try again.',
      startNewHeading: 'Start a new application',
      startNewBody: 'Start a PATH application when you are ready. You can save your progress and come back later.',
      startNewReturningBody: 'You can start another application if you need more PATH support. You can save your progress and come back later.',
      leadEmpty: 'Use this dashboard to start a PATH application and return to it later if you save a draft.',
      bodyEmpty: 'After you submit, you can come back here to check messages, documents, and plan activities.',
      leadDraft: 'You have a saved application in progress.',
      bodyDraft: 'Resume it below when you are ready. If you need to start again, you can delete the saved application first.',
      leadInReview: 'Your application has been submitted.',
      bodyInReview: 'Use this dashboard to check its status and read messages from the program team.',
      leadActionRequired: 'There is an update on your application.',
      bodyActionRequired: 'Review your messages for next steps or any information we still need from you.',
      leadApproved: 'Your PATH support information is available here.',
      bodyApproved: 'Check this dashboard for plan activities, documents, and messages from the program team.',
      leadNotApproved: 'NWAC has an update about your application.',
      bodyNotApproved: 'Please read your messages or contact the program team for details.',
      leadClosedApplication: 'This application is now in your history.',
      bodyClosedApplication: 'You can still review the application details below.',
      leadInterventions: 'Use this dashboard to review your current plan activities and messages.',
      bodyInterventions: 'If we need anything from you, we will contact you here.',
      leadRestart: 'You can review your previous PATH application and activities here.',
      bodyRestart: 'You can also start a new application if you need more PATH support.'
    },
    fr: {
      title: 'Mon tableau de bord PATH',
      start: 'Commencer une demande',
      loading: 'Chargement...',
      unread: 'Vous avez des messages non lus.',
      urgent: 'Vous avez des messages urgents. Veuillez les consulter dès que possible.',
      applicationHeading: 'Demande',
      applicationInReview: 'Votre demande a été reçue. Aucune action n’est requise pour le moment.',
      applicationActionRequired: 'Il y a une mise à jour concernant votre demande. Consultez vos messages pour connaître les prochaines étapes.',
      applicationApproved: 'Les renseignements sur votre soutien PATH en cours sont disponibles ici.',
      applicationNotApproved: 'L’AFAC a une mise à jour concernant votre demande. Veuillez lire vos messages ou communiquer avec nous pour obtenir les détails.',
      applicationClosed: 'Cette demande fait maintenant partie de votre historique. Vous pouvez toujours consulter ses détails ci-dessous.',
      viewMessages: 'Voir les messages',
      savedDraftHeading: 'Demande sauvegardée',
      interventionsHeading: 'Activités du plan en cours',
      interventionsLoading: 'Chargement des activités du plan...',
      interventionsEmpty: 'Aucune activité du plan en cours pour le moment.',
      interventionsError: 'Impossible de charger vos activités du plan. Veuillez réessayer.',
      startNewHeading: 'Commencer une nouvelle demande',
      startNewBody: 'Commencez une demande PATH lorsque vous êtes prêt. Vous pouvez enregistrer votre progression et revenir plus tard.',
      startNewReturningBody: 'Vous pouvez commencer une autre demande si vous avez besoin d’un autre soutien PATH. Vous pouvez enregistrer votre progression et revenir plus tard.',
      leadEmpty: 'Utilisez ce tableau de bord pour commencer une demande PATH et y revenir plus tard si vous enregistrez un brouillon.',
      bodyEmpty: 'Après la soumission, vous pourrez revenir ici pour vérifier vos messages, vos documents et vos activités de plan.',
      leadDraft: 'Vous avez une demande sauvegardée en cours.',
      bodyDraft: 'Reprenez-la ci-dessous lorsque vous êtes prêt. Si vous devez recommencer, vous pouvez d’abord supprimer la demande sauvegardée.',
      leadInReview: 'Votre demande a été soumise.',
      bodyInReview: 'Utilisez ce tableau de bord pour vérifier son état et lire les messages de l’équipe du programme.',
      leadActionRequired: 'Il y a une mise à jour concernant votre demande.',
      bodyActionRequired: 'Consultez vos messages pour connaître les prochaines étapes ou les renseignements qu’il nous manque.',
      leadApproved: 'Les renseignements sur votre soutien PATH sont disponibles ici.',
      bodyApproved: 'Consultez ce tableau de bord pour vos activités de plan, vos documents et les messages de l’équipe du programme.',
      leadNotApproved: 'L’AFAC a une mise à jour concernant votre demande.',
      bodyNotApproved: 'Veuillez lire vos messages ou communiquer avec l’équipe du programme pour obtenir les détails.',
      leadClosedApplication: 'Cette demande fait maintenant partie de votre historique.',
      bodyClosedApplication: 'Vous pouvez toujours consulter les détails de la demande ci-dessous.',
      leadInterventions: 'Utilisez ce tableau de bord pour consulter les activités actuelles de votre plan et vos messages.',
      bodyInterventions: 'Si nous avons besoin de quelque chose de votre part, nous communiquerons avec vous ici.',
      leadRestart: 'Vous pouvez consulter ici votre demande PATH précédente et vos activités.',
      bodyRestart: 'Vous pouvez aussi commencer une nouvelle demande si vous avez besoin d’un autre soutien PATH.'
    }
  };

  const L = t[language] || t.en;
  const applicationCardStatus = normalizeExternalStatus(applicationCardSubmission?.external_status);
  const hasVisibleInterventions = interventions.length > 0;
  const showPlanActivities = interventionsLoading || Boolean(interventionsError) || hasVisibleInterventions;
  const showEmptyState = !showApplicationCard && !showPlanActivities && !visibleDraft;
  const showRestartMessage = showEmptyState && normalizedSubmissions.length > 0;
  const applicationStatusPresentation = showApplicationCard
    ? getParticipantStatusPresentation(applicationCardStatus, language, { hasPlanActivities: hasVisibleInterventions })
    : null;
  const applicationSectionHeading = applicationStatusPresentation?.cardHeading || L.applicationHeading;
  const applicantAccountRequired = schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR && schemaError === 'applicant_account_required';
  const schemaErrorMessage = applicantAccountRequired
    ? (language === 'fr'
      ? 'Ce tableau de bord est réservé aux comptes du portail des demandeurs. Déconnectez-vous, puis reconnectez-vous avec un compte demandeur.'
      : 'This dashboard is for applicant portal accounts. Please sign out, then sign in with an applicant account.')
    : schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR
      ? (language === 'fr'
        ? 'Le schéma de formulaire n’a pas pu être chargé. Réessayez plus tard ou contactez le soutien.'
        : 'The workflow schema could not be loaded. Please try again later or contact support.')
      : (language === 'fr'
        ? 'Aucun schéma de formulaire publié n’est disponible. Publiez le flux dans l’admin avant de lancer une nouvelle demande.'
        : 'No published workflow schema is available. Publish the workflow in admin before starting a new application.');
  const schemaErrorSuffix = schemaError && schemaStatus === WORKFLOW_SCHEMA_STATUS.ERROR && !applicantAccountRequired
    ? ` (${schemaError})`
    : '';
  const introCopy = showApplicationCard
      ? {
          lead: applicationStatusPresentation?.dashboardLead || L.leadInReview,
          body: applicationStatusPresentation?.dashboardBody || L.bodyInReview,
        }
      : hasVisibleInterventions
        ? { lead: L.leadInterventions, body: L.bodyInterventions }
      : visibleDraft
        ? { lead: L.leadDraft, body: L.bodyDraft }
        : showRestartMessage
          ? { lead: L.leadRestart, body: L.bodyRestart }
          : { lead: L.leadEmpty, body: L.bodyEmpty };
  const startButtonLabel = L.start;
  const startNewBody = normalizedSubmissions.length > 0 || hasVisibleInterventions
    ? L.startNewReturningBody
    : L.startNewBody;

  return (
    <div className="govuk-width-container govuk-!-margin-top-3">
      <main className="govuk-main-wrapper" role="main">
        <h1 className="govuk-heading-l">{L.title}</h1>
        <p className="govuk-body govuk-!-margin-bottom-1">{introCopy.lead}</p>
        <p className="govuk-body govuk-!-margin-bottom-5">{introCopy.body}</p>

        {schemaLoading && !schemaReady && !schemaErrorState && (
          <p className="govuk-hint">
            {language === 'fr'
              ? 'Chargement du schéma de formulaire dynamique...'
              : 'Loading the dynamic workflow schema...'}
          </p>
        )}

        {schemaErrorState && (
          <div className="govuk-warning-text govuk-!-margin-bottom-4">
            <span className="govuk-warning-text__icon" aria-hidden="true">!</span>
            <strong className="govuk-warning-text__text">
              {schemaErrorMessage}
              {schemaErrorSuffix}
            </strong>
            {!applicantAccountRequired && (
              <div className="govuk-!-margin-top-2">
                <button type="button" className="govuk-button govuk-button--secondary" onClick={() => refreshSchema()}>
                  {language === 'fr' ? 'Réessayer le chargement' : 'Try loading again'}
                </button>
              </div>
            )}
          </div>
        )}

        {bannerVisible && unreadCount > 0 && !suppressMessageBanner && (
          <div className={`govuk-notification-banner ${hasUrgentMessages ? 'govuk-notification-banner--urgent' : ''}`} role="alert">
            <div className="govuk-notification-banner__content">
              <p className="govuk-notification-banner__heading">
                {hasUrgentMessages ? L.urgent : L.unread}
              </p>
              <div>
                <Link to="/messages" className="govuk-link">{language === 'fr' ? 'Voir les messages' : 'View Messages'}</Link>
                {' | '}
                <button type="button" className="govuk-link" style={{ background: 'none', border: 'none', padding: 0 }} onClick={() => setBannerVisible(false)}>
                  {language === 'fr' ? 'Fermer' : 'Close'}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && <p className="govuk-body">{L.loading}</p>}
        {error && !loading && <p className="govuk-error-message">{error}</p>}

        {!loading && !error && (
          <>
            {visibleDraft && (
              <section className="govuk-!-margin-bottom-7">
                <h2 className="govuk-heading-m">{L.savedDraftHeading}</h2>
                <ApplicationCard
                  application={{ ...visibleDraft, status: 'draft', dynamic: true }}
                  language={language}
                  onResumeDraft={handleResumeDraft}
                  onDeleteDraft={handleDeleteRequest}
                  deleteDisabled={deleteBusy}
                />
              </section>
            )}

            {showApplicationCard && applicationCardSubmission && (
              <section className="govuk-!-margin-bottom-7">
                <h2 className="govuk-heading-m">{applicationSectionHeading}</h2>
                <ApplicationCard
                  application={{
                    tracking_id: applicationCardSubmission.tracking_id,
                    application_id: applicationCardSubmission.application_id,
                    detail_available: applicationCardSubmission.detail_available,
                    external_status: applicationCardSubmission.external_status,
                    created_at: applicationCardSubmission.created_at,
                    last_updated: applicationCardSubmission.last_updated || applicationCardSubmission.created_at,
                    dynamicSubmission: true
                  }}
                  language={language}
                  statusPresentation={applicationStatusPresentation}
                />
              </section>
            )}

            {showPlanActivities && (
              <section className="govuk-!-margin-bottom-7">
                <h2 className="govuk-heading-m">{L.interventionsHeading}</h2>
                {interventionsLoading && <p className="govuk-body">{L.interventionsLoading}</p>}
                {interventionsError && <p className="govuk-error-message">{interventionsError}</p>}
                {!interventionsLoading && !interventionsError && interventions.map((intervention) => (
                  <InterventionCard
                    key={intervention.intervention_id || `${intervention.tracking_id}-${intervention.type_code || 'intervention'}`}
                    intervention={intervention}
                    language={language}
                  />
                ))}
              </section>
            )}

            {showStartButton && (
              <section className="govuk-!-margin-bottom-7">
                <h2 className="govuk-heading-m">{L.startNewHeading}</h2>
                <p className="govuk-body">{startNewBody}</p>
                <div className="govuk-button-group">
                  <button
                    className="govuk-button"
                    onClick={handleStart}
                    disabled={!schemaReady || schemaLoading || loading}
                  >
                    {startButtonLabel}
                  </button>
                </div>
              </section>
            )}

            {!schemaReady && !schemaErrorState && (showStartButton || visibleDraft) && (
              <p className="govuk-hint govuk-!-margin-bottom-6">
                {language === 'fr'
                  ? 'Les étapes du formulaire se chargent. Merci de patienter avant de commencer ou de reprendre une demande.'
                  : 'The form steps are still loading. Please wait before starting or resuming an application.'}
              </p>
            )}

          </>
        )}
      </main>
      {showDraftModal && (
        <div
          className="govuk-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-draft-modal-title"
          aria-describedby="dashboard-draft-modal-body"
          tabIndex="-1"
          ref={modalRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            className="govuk-modal__content"
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '520px',
              width: '100%',
              outline: 'none'
            }}
          >
            <h2 className="govuk-heading-m" id="dashboard-draft-modal-title">
              {modalCopy.title}
            </h2>
            <p className="govuk-body" id="dashboard-draft-modal-body">
              {modalCopy.body}
            </p>
            {modalError && (
              <p className="govuk-error-message" role="alert">
                {modalError}
              </p>
            )}
            <div className="govuk-button-group govuk-!-margin-top-4">
              <button
                type="button"
                className="govuk-button"
                onClick={handleModalContinue}
                disabled={modalBusy}
              >
                {modalCopy.resume}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--warning"
                onClick={handleModalStartFresh}
                disabled={modalBusy}
              >
                {modalCopy.startOver}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                onClick={handleModalCancel}
                disabled={modalBusy}
              >
                {modalCopy.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteModal && (
        <div
          className="govuk-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-delete-modal-title"
          aria-describedby="dashboard-delete-modal-body"
          tabIndex="-1"
          ref={deleteModalRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            className="govuk-modal__content"
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '420px',
              width: '100%',
              outline: 'none'
            }}
          >
            <h2 className="govuk-heading-m" id="dashboard-delete-modal-title">
              {deleteModalCopy.title}
            </h2>
            <p className="govuk-body" id="dashboard-delete-modal-body">
              {deleteModalCopy.body}
            </p>
            {deleteError && (
              <p className="govuk-error-message" role="alert">
                {deleteError}
              </p>
            )}
            <div className="govuk-button-group govuk-!-margin-top-4">
              <button
                type="button"
                className="govuk-button govuk-button--warning"
                onClick={handleDeleteConfirm}
                disabled={deleteBusy}
              >
                {deleteModalCopy.confirm}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                onClick={handleDeleteCancel}
                disabled={deleteBusy}
              >
                {deleteModalCopy.cancel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDashboard;
